import React, { useState, useRef, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { findLast, range, map, compose, pathOr, has, isNil, prop } from 'ramda'
import { usePrevious } from 'hooks'
import { mapIndexed } from 'utils/functional'
import strings from 'utils/strings'

import Tab from './Tab'

import {
  Wrapper,
  Container,
  AddIcon,
  LeftIcon,
  RightIcon,
} from './DynamicTabs.style'

const setScrollPositionToEnd = (ref) => {
  setTimeout(() => {
    const width = pathOr(0, ['current', 'scrollWidth'], ref)

    if (has('current', ref) && !isNil(prop('current', ref))) {
      ref.current.scrollLeft = width
    }
  }, 1) // nextTick sort of thing
}

const createRefs = compose(map(React.createRef), range(1))

/**
 *
 * DynamicTabs
 *
 * if the scelected item is null, then we show a new Tab labeled 'Undefined'
 */
const DynamicTabs = ({
  className,
  items,
  selectedItem,
  onAdd,
  onClick,
  onRemove,
  editable,
}) => {
  const [showArrows, setShowArrows] = useState(false)

  const numItems = items.length
  const prevousNumItems = usePrevious(numItems)

  const tabRefs = useMemo(() => createRefs(numItems), [numItems])
  const tabsContainerRef = useRef(null)
  const wrapperRef = useRef(null)

  useEffect(() => {
    const containerWidth = wrapperRef.current.offsetWidth
    const tabsScrollWidth = tabsContainerRef.current.scrollWidth

    setShowArrows(tabsScrollWidth > containerWidth)
  }, [wrapperRef, tabsContainerRef, items, selectedItem])

  // scroll to end if item is added
  useEffect(() => {
    if (numItems > prevousNumItems) {
      setScrollPositionToEnd(tabsContainerRef)
    }
  }, [tabsContainerRef, numItems, prevousNumItems])

  // scroll to end if adding an item
  useEffect(() => {
    if (selectedItem === null) {
      setScrollPositionToEnd(tabsContainerRef)
    }
  }, [selectedItem])

  const scrollLeft = () => {
    const tabsWidth = tabsContainerRef.current.offsetWidth
    const currentScroll = tabsContainerRef.current.scrollLeft

    // find the first tab that is visible on the left side
    const scrollToLeft = tabRefs.find((ref) => {
      return ref.current.offsetLeft + ref.current.offsetWidth >= currentScroll
    })

    // scroll found item to the right-most position
    tabsContainerRef.current.scroll({
      left:
        scrollToLeft.current.offsetLeft -
        tabsWidth +
        scrollToLeft.current.offsetWidth,
      behavior: 'smooth',
    })
  }

  const scrollRight = () => {
    const tabsWidth = tabsContainerRef.current.offsetWidth
    const currentScroll = tabsContainerRef.current.scrollLeft

    // find the last tab that is visible on the right side
    const scrollToLeft = findLast((ref) => {
      return currentScroll + tabsWidth > ref.current.offsetLeft
    }, tabRefs)

    // scroll found item to the left-most position
    tabsContainerRef.current.scroll({
      left: scrollToLeft.current.offsetLeft,
      behavior: 'smooth',
    })
  }

  const renderTabs = mapIndexed(({ key, title, subtitle }, index) => {
    return (
      <Tab
        ref={tabRefs[index]}
        key={key}
        onClick={() => onClick(key)}
        onRemove={() => onRemove(key)}
        selected={selectedItem === key}
        title={title}
        subtitle={subtitle}
        editable={editable}
      />
    )
  })

  return (
    <Wrapper ref={wrapperRef} className={className}>
      {showArrows && <LeftIcon onClick={scrollLeft} />}

      <Container ref={tabsContainerRef} showArrows={showArrows}>
        {renderTabs(items)}
        {selectedItem === null && (
          <Tab title={strings.undefined} isUndefined selected />
        )}
      </Container>

      {showArrows && <RightIcon onClick={scrollRight} />}
      {selectedItem !== null && editable && <AddIcon onClick={onAdd} />}
    </Wrapper>
  )
}

DynamicTabs.defaultProps = {
  onClick: () => {},
  onAdd: () => {},
  onRemove: () => {},
}

DynamicTabs.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func,
  onAdd: PropTypes.func,
  onRemove: PropTypes.func,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      title: PropTypes.string,
      subtitle: PropTypes.string,
    })
  ),
  selectedItem: PropTypes.string,
  editable: PropTypes.bool,
}

export default DynamicTabs
